import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { baseurl } from "../../baseurl";
import { 
  HiOutlineClock, HiOutlineShieldCheck, 
  HiOutlineBriefcase, HiOutlineCheckCircle,
  HiOutlineCake, HiOutlineUserGroup
} from "react-icons/hi2";
import { FaPlane } from "react-icons/fa";

const FlightDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [flight, setFlight] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [resultToken, setResultToken] = useState("");
  
  // Loader States
  const [isInitialLoading, setIsInitialLoading] = useState(true); 
  const [fareLoading, setFareLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);

  const [updatedFare, setUpdatedFare] = useState(null);
  const [extraServices, setExtraServices] = useState(null);
  const [activeTab, setActiveTab] = useState("baggage");
  
  // Selection States
  const [selectedBaggage, setSelectedBaggage] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [selectedSeat, setSelectedSeat] = useState(null);

  const THEME_COLOR = "#166070";

  useEffect(() => {
    const init = async () => {
      try {
        const rawFlight = searchParams.get("flight");
        const rawSearch = searchParams.get("search");
        const rawToken = searchParams.get("token");

        if (rawFlight && rawSearch) {
          const parsedFlight = JSON.parse(decodeURIComponent(rawFlight));
          const parsedSearch = JSON.parse(decodeURIComponent(rawSearch));
          const finalToken = (rawToken && rawToken !== "undefined") 
                             ? decodeURIComponent(rawToken) 
                             : parsedFlight.ResultToken;

          setFlight(parsedFlight);
          setSearchData(parsedSearch);
          setResultToken(finalToken);
          
          if (finalToken) {
            await Promise.all([
              updateFareQuote(finalToken),
              fetchExtraServices(finalToken)
            ]);
          }
        }
      } catch (err) {
        console.error("Initialization Error:", err);
      } finally {
        setIsInitialLoading(false);
      }
    };
    init();
  }, [searchParams]);

  const updateFareQuote = async (token) => {
    setFareLoading(true);
    try {
      const res = await axios.post(`${baseurl}/updateFareQuote`, { ResultToken: token });
      if (res.data?.success) {
        setUpdatedFare(res.data?.data?.UpdateFareQuote?.FareQuoteDetails);
      }
    } catch (err) { 
      console.error("Fare Error:", err); 
    } finally { 
      setFareLoading(false); 
    }
  };

  const fetchExtraServices = async (token) => {
    setServicesLoading(true);
    try {
      const res = await axios.post(`${baseurl}/getExtraServices`, { ResultToken: token });
      if (res.data?.success) {
        setExtraServices(res.data?.data?.ExtraServices?.ExtraServiceDetails);
      }
    } catch (err) { 
      console.error("Services Error:", err); 
    } finally { 
      setServicesLoading(false); 
    }
  };

  // --- FULL SCREEN LOADER COMPONENT ---
  if (isInitialLoading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
        <div className="spinner-grow" style={{ width: '3rem', height: '3rem', color: THEME_COLOR }} role="status"></div>
        <h5 className="mt-4 fw-bold" style={{ color: THEME_COLOR }}>Validating Live Fares...</h5>
        <p className="text-muted small">We're securing the best price and fetching options for you.</p>
      </div>
    );
  }

  if (!flight || !searchData) {
    return <div className="container py-5 text-center"><h4>Flight details not found.</h4></div>;
  }

  const displaySegments = updatedFare?.FlightDetails?.Details[0] || flight.FlightDetails.Details[0];
  const displayPrice = updatedFare?.Price || flight.Price;
  const firstSegment = displaySegments[0];
  const lastSegment = displaySegments[displaySegments.length - 1];

  const addOnTotal = (selectedBaggage?.Price || 0) + (selectedMeal?.Price || 0) + (selectedSeat?.Price || 0);
  const finalPayable = Math.round((displayPrice?.TotalDisplayFare || 0) + addOnTotal);

  // Helper method to look up matched seat identifier
  const isSeatSelected = (seat) => {
    if (!selectedSeat) return false;
    const currentSeatId = seat.SeatId || `${seat.RowNumber}-${seat.SeatNumber}`;
    const selectedSeatId = selectedSeat.SeatId || `${selectedSeat.RowNumber}-${selectedSeat.SeatNumber}`;
    return currentSeatId === selectedSeatId;
  };

  // Helper validation matching rule for seat availability mapping
  const isSeatAvailable = (seat) => {
    return seat.AvailablityType === 1 || Number(seat.Price) === 0 || seat.Selectable === true;
  };

  return (
    <div className="container py-lg-5 py-3 animate-fade-in">
      <div className="row g-4">
        <div className="col-lg-8">
          {/* FLIGHT ITINERARY CARD */}
          <h5 className="fw-bold mb-3 d-flex align-items-center" style={{ color: THEME_COLOR }}>
            <HiOutlineShieldCheck className="me-2" /> Flight Details
          </h5>
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "16px", overflow: 'hidden' }}>
             <div className="p-3 text-white d-flex justify-content-between align-items-center" style={{ background: THEME_COLOR }}>
                <span className="fw-bold">{firstSegment.Origin.CityName} → {lastSegment.Destination.CityName}</span>
                <span className="badge bg-white text-dark py-2 px-3">{firstSegment.CabinClass === "RR" ? "Economy" : "Business"}</span>
             </div>
             <div className="p-4">
                {displaySegments.map((s, i) => (
                    <div key={i} className={`d-flex justify-content-between align-items-center text-center ${i !== 0 ? 'mt-4 pt-4 border-top' : ''}`}>
                        <div className="text-start">
                            <div className="fw-bold h4 mb-0">{s.Origin.DateTime.split(' ')[1].substring(0,5)}</div>
                            <div className="fw-bold text-uppercase" style={{ color: THEME_COLOR }}>{s.Origin.AirportCode}</div>
                            <div className="x-small text-muted">{s.Origin.CityName}</div>
                        </div>
                        <div className="flex-grow-1 px-4">
                            <div className="x-small text-muted mb-1"><HiOutlineClock /> {s.Duration}m</div>
                            <div className="position-relative d-flex align-items-center justify-content-center">
                                <div style={{ height: '1px', backgroundColor: '#dee2e6', width: '100%' }}></div>
                                <FaPlane className="position-absolute bg-white px-1" style={{color: THEME_COLOR, fontSize: '18px'}} />
                            </div>
                        </div>
                        <div className="text-end">
                            <div className="fw-bold h4 mb-0">{s.Destination.DateTime.split(' ')[1].substring(0,5)}</div>
                            <div className="fw-bold text-uppercase" style={{ color: THEME_COLOR }}>{s.Destination.AirportCode}</div>
                            <div className="x-small text-muted">{s.Destination.CityName}</div>
                        </div>
                    </div>
                ))}
             </div>
          </div>

          {/* CUSTOMISE TRIP SECTION */}
          <h5 className="fw-bold mb-3 d-flex align-items-center" style={{ color: THEME_COLOR }}>
            <HiOutlineUserGroup className="me-2" /> Customise Your Trip
          </h5>
          
          <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "16px", overflow: 'hidden' }}>
            <div className="d-flex border-bottom bg-light overflow-auto hide-scrollbar">
              {[
                { id: "baggage", label: "Extra Baggage", icon: <HiOutlineBriefcase /> },
                { id: "meals", label: "Meals", icon: <HiOutlineCake /> },
                { id: "seats", label: "Preferred Seats", icon: <HiOutlineUserGroup /> }
              ].map(tab => (
                <button 
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className="btn border-0 rounded-0 px-4 py-3 flex-shrink-0 d-flex align-items-center gap-2"
                  style={{ 
                    borderBottom: activeTab === tab.id ? `3px solid ${THEME_COLOR}` : "3px solid transparent",
                    color: activeTab === tab.id ? THEME_COLOR : "#6c757d",
                    background: activeTab === tab.id ? '#fff' : 'transparent',
                    fontWeight: activeTab === tab.id ? "bold" : "normal"
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>

            <div className="p-3" style={{ minHeight: '200px', maxHeight: "400px", overflowY: "auto" }}>
              {servicesLoading ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                   <div className="spinner-border text-secondary" role="status"></div>
                   <p className="mt-2 text-muted small">Loading available options...</p>
                </div>
              ) : (
                <div className="animate-fade-in">
                  {/* Baggage Panel */}
                  {activeTab === "baggage" && (extraServices?.Baggage?.[0]?.length > 0 ? extraServices.Baggage[0].map((bg, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => setSelectedBaggage(selectedBaggage?.BaggageId === bg.BaggageId ? null : bg)} 
                      className={`d-flex justify-content-between align-items-center p-3 mb-2 rounded-3 border cursor-pointer ${selectedBaggage?.BaggageId === bg.BaggageId ? 'border-success bg-gray bg-opacity-10' : ''}`}
                    >
                      <div><div className="fw-bold">{bg.Weight} Extra</div><small className="text-muted">Available for {bg.Origin}-{bg.Destination}</small></div>
                      <div className="text-end"><div className="fw-bold text-success">₹{bg.Price}</div>{selectedBaggage?.BaggageId === bg.BaggageId && <HiOutlineCheckCircle className="text-success fs-5" />}</div>
                    </div>
                  )) : <div className="text-center py-4 text-muted">No extra baggage options available.</div>)}

                  {/* Meals Panel */}
                  {activeTab === "meals" && (extraServices?.Meals?.[0]?.length > 0 ? extraServices.Meals[0].map((ml, idx) => (
                    <div 
                      key={idx} 
                      onClick={() => setSelectedMeal(selectedMeal?.MealId === ml.MealId ? null : ml)} 
                      className={`d-flex justify-content-between align-items-center p-3 mb-2 rounded-3 border cursor-pointer ${selectedMeal?.MealId === ml.MealId ? 'border-success bg-success bg-opacity-10' : ''}`}
                    >
                      <div className="pe-3"><div className="fw-bold">{ml.Description}</div><small className="text-muted">{ml.Origin} → {ml.Destination}</small></div>
                      <div className="text-end flex-shrink-0"><div className="fw-bold text-success">₹{ml.Price}</div>{selectedMeal?.MealId === ml.MealId && <HiOutlineCheckCircle className="text-success fs-5" />}</div>
                    </div>
                  )) : <div className="text-center py-4 text-muted">No meal pre-booking available.</div>)}

                  {/* Seats Panel */}
                  {activeTab === "seats" && (extraServices?.Seat?.[0] ? extraServices.Seat[0].map((row, rIdx) => 
                    row.map((seat, sIdx) => {
                      const available = isSeatAvailable(seat);
                      const selected = isSeatSelected(seat);
                      
                      return (
                        <div 
                          key={`${rIdx}-${sIdx}`} 
                          onClick={() => available && setSelectedSeat(selected ? null : seat)} 
                          className={`d-flex justify-content-between align-items-center p-3 mb-2 rounded-3 border ${!available ? 'opacity-50 bg-light' : 'cursor-pointer'} ${selected ? 'border-success bg-success bg-opacity-10' : ''}`}
                        >
                          <div>
                            <span className="badge me-2" style={{background: THEME_COLOR}}>Row {seat.RowNumber}</span>
                            <span className="fw-bold">Seat {seat.SeatNumber}</span>
                          </div>
                          <div className="text-end">
                            <span className="fw-bold text-success">
                              {Number(seat.Price) === 0 ? "Free" : `₹${seat.Price}`}
                            </span>
                            {selected && <HiOutlineCheckCircle className="ms-2 text-success fs-5" />}
                          </div>
                        </div>
                      );
                    })
                  ) : <div className="text-center py-4 text-muted">Seat map not available for this segment.</div>)}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN: PRICE SUMMARY */}
        <div className="col-lg-4">
          <div className="sticky-top" style={{ top: "100px" }}>
            <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: "16px" }}>
              <div className="p-4 text-white d-flex justify-content-between align-items-center" style={{ background: THEME_COLOR }}>
                <h5 className="mb-0 fw-bold">Price Summary</h5>
                {fareLoading && <div className="spinner-border spinner-border-sm" role="status"></div>}
              </div>
              <div className="p-4">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Base Fare & Taxes</span>
                  <span className="fw-bold">₹{(displayPrice?.TotalDisplayFare || 0).toLocaleString()}</span>
                </div>
                
                {addOnTotal > 0 && (
                  <div className="bg-light p-3 rounded-3 mb-3 border border-dashed border-success">
                    <small className="text-success fw-bold d-block mb-2">Extra Services:</small>
                    {selectedBaggage && <div className="d-flex justify-content-between x-small mb-1"><span>Baggage ({selectedBaggage.Weight})</span><span>+₹{selectedBaggage.Price}</span></div>}
                    {selectedMeal && <div className="d-flex justify-content-between x-small mb-1"><span>Meal Selection</span><span>+₹{selectedMeal.Price}</span></div>}
                    {selectedSeat && <div className="d-flex justify-content-between x-small"><span>Seat {selectedSeat.SeatNumber}</span><span>+₹{selectedSeat.Price}</span></div>}
                  </div>
                )}

                <hr className="my-3 opacity-10" />
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h5 className="mb-0 fw-bold">Total Payable</h5>
                        <small className="text-muted">All inclusive</small>
                    </div>
                    <h3 className="mb-0 fw-bold" style={{ color: THEME_COLOR }}>₹{finalPayable.toLocaleString()}</h3>
                </div>

                <button 
                  disabled={fareLoading || servicesLoading}
                  className="btn btn-warning w-100 fw-bold py-3 text-uppercase shadow-sm border-0"
                  style={{ borderRadius: '12px', background: '#ffb700' }}
                  onClick={() => navigate('/passenger-details', { 
                    state: { 
                      flight: updatedFare || flight, 
                      searchData, 
                      extraServices,
                      resultToken, 
                      selectedAddons: { selectedBaggage, selectedMeal, selectedSeat },
                      finalPayable
                    } 
                  })}
                >
                  {fareLoading ? "Updating Price..." : "Continue to Passengers"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .cursor-pointer { cursor: pointer; }
        .x-small { font-size: 11px; }
        .animate-fade-in { animation: fadeIn 0.4s ease-in; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default FlightDetails;